import type { ReactNode } from 'react'
import styles from '@/styles/pages/governance/GovernanceListing.module.scss'

export interface GovernanceListingSectionProps {
  /** Section heading (e.g. list title). Omit when the title is rendered above the panel. */
  title?: ReactNode
  description?: ReactNode
  meta?: ReactNode
  /** Right column: filters / actions (Reports-style). */
  toolbar?: ReactNode
  children: ReactNode
  className?: string
}

function hasTitleContent(title: ReactNode): boolean {
  if (title == null || title === false) return false
  if (typeof title === 'string') return title.trim().length > 0
  return true
}

/**
 * Bordered panel: optional 2-column header (intro | toolbar) + body (table, etc.).
 * With `toolbar` and no `title`/`description`/`meta`, only the toolbar row is shown inside the header.
 */
export function GovernanceListingSection({
  title,
  description,
  meta,
  toolbar,
  children,
  className = '',
}: GovernanceListingSectionProps) {
  const hasToolbar = toolbar != null && toolbar !== false
  const hasDesc = description != null && description !== false
  const hasMeta = meta != null && meta !== false
  const showTitle = hasTitleContent(title)
  const showIntro = showTitle || hasDesc || hasMeta

  const intro = (
    <>
      {showTitle && <h2 className={styles.panelTitle}>{title}</h2>}
      {hasDesc && <p className={styles.panelSubtitle}>{description}</p>}
      {hasMeta && <p className={styles.panelMeta}>{meta}</p>}
    </>
  )

  return (
    <section className={`${styles.panel} ${className}`.trim()}>
      {hasToolbar ? (
        showIntro ? (
          <div className={styles.panelHeader}>
            <div className={styles.panelIntro}>{intro}</div>
            <div className={styles.panelToolbar}>{toolbar}</div>
          </div>
        ) : (
          <div className={styles.panelHeaderToolbarOnly}>{toolbar}</div>
        )
      ) : (
        showIntro && <div className={`${styles.panelIntro} ${styles.panelIntroOnly}`}>{intro}</div>
      )}
      <div className={styles.panelBody}>{children}</div>
    </section>
  )
}
